import React from 'react'
import type { GamingHistoryProps } from '@/features/enrollment'

export default function GamingHistory({ enrollmentData, updateEnrollmentData, errors, hoursPerDayAutoSelected, setHoursPerDayAutoSelected, validateHoursPerDay }: GamingHistoryProps) {
  return (
    <>
      {/* First Gaming Date */}
      <div>
        <label className="block text-sm font-medium text-accent-900 mb-2">
          Estimate the date that injured party first started playing video games? <span className="text-red-500">*</span>
          {enrollmentData.firstGamingDate && enrollmentData.birthYear && enrollmentData.ageStartedGaming && (
            <span className="ml-2 text-xs text-gray-500">
              (Auto-calculated from age data)
            </span>
          )}
        </label>
        <input 
          type="date" 
          name="firstGamingDate"
          className={`input-field w-full ${errors.firstGamingDate ? 'border-red-500' : ''}`} 
          value={enrollmentData.firstGamingDate || ''} 
          onChange={(e) => updateEnrollmentData('firstGamingDate', e.target.value)} 
        />
        {errors.firstGamingDate && (
          <p className="mt-1 text-sm text-red-600">{errors.firstGamingDate}</p>
        )}
      </div>

      {/* Average Hours Per Day */}
      <div>
        <label className="block text-sm font-medium text-accent-900 mb-2">
          On average how many hours a day do you play video games? <span className="text-red-500">*</span>
        </label>
        
        {/* Auto-selection indicator */}
        {enrollmentData.averageHoursPerDay && enrollmentData.hoursPerDay && enrollmentData.averageHoursPerDay === enrollmentData.hoursPerDay && (
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Auto-populated from Step 2:</span>
              <span className="ml-2 text-blue-600 font-semibold">{enrollmentData.averageHoursPerDay} hours</span>
            </p>
            <p className="text-xs text-blue-600 mt-1">
              You can change this number if needed.
            </p>
          </div>
        )}
        
        <input 
          type="number" 
          name="averageHoursPerDay"
          min="3"
          max="24"
          step="0.5"
          className={`input-field w-full ${errors.averageHoursPerDay ? 'border-red-500' : ''} ${enrollmentData.averageHoursPerDay && enrollmentData.hoursPerDay && enrollmentData.averageHoursPerDay === enrollmentData.hoursPerDay ? 'border-blue-300 bg-blue-50' : ''}`} 
          placeholder="e.g., 4" 
          value={enrollmentData.averageHoursPerDay || ''} 
          onChange={(e) => {
            const value = e.target.value
            updateEnrollmentData('averageHoursPerDay', value)
            validateHoursPerDay(value)
            // Prevent auto-selection from interfering with user changes
            if (!hoursPerDayAutoSelected) {
              setHoursPerDayAutoSelected(true)
            }
          }} 
        />
        {errors.averageHoursPerDay && (
          <p className="mt-1 text-sm text-red-600">{errors.averageHoursPerDay}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">Enter the number of hours (e.g., 4 for 4 hours, 2.5 for 2.5 hours). Minimum 3 hours required.</p>
      </div>

      {/* First Video Game */}
      <div>
        <label className="block text-sm font-medium text-accent-900 mb-2">
          What video game that was played first? <span className="text-red-500">*</span>
        </label>
        
        {/* Auto-selection indicator */}
        {['GTA', 'Grand Theft Auto', 'Roblox', 'Minecraft', 'Fortnite'].includes(enrollmentData.firstVideoGame) && (
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Auto-selected:</span>
              <span className="ml-2 text-blue-600 font-semibold">{enrollmentData.firstVideoGame}</span>
            </p>
            <p className="text-xs text-blue-600 mt-1">
              You can change this to any other game name if needed.
            </p>
          </div>
        )}
        
        <input 
          type="text" 
          name="firstVideoGame"
          className={`input-field w-full ${errors.firstVideoGame ? 'border-red-500' : ''} ${['GTA', 'Grand Theft Auto', 'Roblox', 'Minecraft', 'Fortnite'].includes(enrollmentData.firstVideoGame) ? 'border-blue-300 bg-blue-50' : ''}`} 
          placeholder="e.g., Minecraft" 
          value={enrollmentData.firstVideoGame || ''} 
          onChange={(e) => updateEnrollmentData('firstVideoGame', e.target.value)} 
        />
        {errors.firstVideoGame && (
          <p className="mt-1 text-sm text-red-600">{errors.firstVideoGame}</p>
        )}
      </div>

      {/* Game Details */}
      <div>
        <label className="block text-sm font-medium text-accent-900 mb-2">
          Please list the game names including the version, dates, and estimated hours spent playing each of the above game. (optional)
        </label>
        <textarea 
          className="input-field w-full h-32" 
          placeholder="Example: Roblox from March 2022 to present, 3 hours/day; Call of Duty: Modern Warfare II from January 2023- March 2023, total 100 hours." 
          value={enrollmentData.gameDetails || ''} 
          onChange={(e) => updateEnrollmentData('gameDetails', e.target.value)} 
        />
        <p className="text-xs text-accent-500 mt-1">
          Example: Roblox from March 2022 to present, 3 hours/day; Call of Duty: Modern Warfare II from January 2023- March 2023, total 100 hours.
        </p>
      </div>
    </>
  )
}